<template>
  <div v-if="data && data.length" class="dialog" :class="{ 'single-select-mode': singleSelect }">
    <div v-if="searchable" class="search-bar">
      <div class="search-icon">🔍</div>
      <input 
        v-model="search" 
        type="text" 
        :placeholder="$t('search') || 'Search...'" 
        class="search-input-field"
        @click.stop
      />
      <span class="close-btn" @click.stop="$emit('close')" :title="$t('close') || 'Close'">×</span>
    </div>
    <ul>
      <li
        class="item"
        v-for="(item, idx) in showAllOption ? [fieldName ? { [fieldName]: 'All', id: 'All'} : 'All' , ...itemsToShow] : itemsToShow"
        :key="getItemKey(item, idx)"
        @click="handleInput(item)"
        :class="{ 'is-selected': isSelected(item) }"
      >
        <!--
          Checkbox is display-only for selection state. Handle the click here with
          prevent+stop so the native checkbox input and the parent <li> do not both
          fire handleInput (which previously toggled off then immediately back on).
        -->
        <Checkbox
          v-if="!singleSelect"
          :value="isSelected(item)"
          @click.native.prevent.stop="handleInput(item)"
        />
        <span>{{fieldName ? item[fieldName] : item }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
import Checkbox from "@/modules/common/components/Checkbox";
export default {
  props: {
    searchable: { type: Boolean, default: true },
    data: { type: Array, reqired: true },
    initialSelected: { type: Array, reqired: false },
    fieldName: { type: String, default: "" },
    idFieldName: { type: String, required: false },
    showAllOption: { type: Boolean, default: true },
    singleSelect: { type: Boolean, default: false }
  },
  data() {
    return {
      search: "",
      selected: [],
      isAllActive:
        this.initialSelected && this.initialSelected.length === this.data.length ? true : false
    };
  },
  created() {
    this.syncSelectedFromProp(this.initialSelected);
  },
  watch: {
    initialSelected: {
      handler(newVal) {
        this.syncSelectedFromProp(newVal);
      },
      deep: true,
    },
  },
  computed: {
    itemsToShow() {
      const searchStr = (this.search || '').trim().toLowerCase();
      if (!searchStr) return this.data || [];
      return (this.data || []).filter((item) => {
        const label = this.fieldName ? item && item[this.fieldName] : item;
        return String(label || '').toLowerCase().includes(searchStr);
      });
    },
    areAllVisibleSelected() {
      if (!this.itemsToShow.length) return false;
      return this.itemsToShow.every((item) => this.findSelectedIndex(item) >= 0);
    }
  },
  methods: {
    getItemKey(item, idx) {
      if (this.isAllItem(item)) return "all";
      if (this.idFieldName && item && item[this.idFieldName] != null) {
        return String(item[this.idFieldName]);
      }
      return idx;
    },
    idsMatch(a, b) {
      if (a == null || b == null) return false;
      return String(a) === String(b);
    },
    syncSelectedFromProp(initialSelected) {
      if (!initialSelected || !initialSelected.length) {
        this.selected = [];
        this.isAllActive = false;
        return;
      }
      this.selected = initialSelected.slice();
      this.isAllActive = this.data.length > 0 && initialSelected.length === this.data.length;
    },
    isAllItem(item) {
      return item === "All" || (item && item.id === "All");
    },
    isSelected(item) {
      const { selected, idFieldName } = this;
      if (this.isAllItem(item)) return this.areAllVisibleSelected;
      return selected.some(a => {
        return idFieldName ? this.idsMatch(a[idFieldName], item[idFieldName]) : a === item;
      });
    },
    findSelectedIndex(item) {
      const { idFieldName } = this;
      return this.selected.findIndex(selected => {
        if (idFieldName) return this.idsMatch(selected[idFieldName], item[idFieldName]);
        return selected === item;
      });
    },
    findVisibleIndex(item) {
      const { idFieldName } = this;
      return this.itemsToShow.findIndex((visible) => {
        if (idFieldName) return this.idsMatch(visible[idFieldName], item[idFieldName]);
        return visible === item;
      });
    },
    handleInput(item) {
      const { singleSelect } = this;
      if (this.isAllItem(item)) {
        if (this.areAllVisibleSelected) {
          this.selected = this.selected.filter((selected) => this.findVisibleIndex(selected) < 0);
        } else {
          this.itemsToShow.forEach((visible) => {
            if (this.findSelectedIndex(visible) < 0) this.selected.push(visible);
          });
        }
        this.isAllActive = this.data.length > 0 && this.selected.length === this.data.length;
      } else {
        if (singleSelect) {
          // Single-select mode: replace selection with clicked item
          const itemIdx = this.findSelectedIndex(item);
          if (itemIdx >= 0) {
            // If clicking the same item, deselect it
            this.selected = [];
            this.isAllActive = false;
          } else {
            // Select only this item
            this.selected = [item];
            this.isAllActive = false;
          }
        } else {
          // Multi-select mode: toggle selection
          const itemIdx = this.findSelectedIndex(item);
          if (itemIdx >= 0) {
            this.selected.splice(itemIdx, 1);
            this.isAllActive = false;
          } else {
            this.selected.push(item);
            if (this.selected.length === this.data.length)
              this.isAllActive = true;
          }
        }
      }
      // Emit a copy so parent state is not shared/mutated with dialog internals
      this.$emit("selected", this.selected.slice());
      // Note: Don't close dialog automatically - let user select multiple items
    }
  },
  components: {
    Checkbox
  }
};
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;
.dialog {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  background-color: $white;
  z-index: 11;
  border-radius: 6px;
  overflow: hidden;
  min-width: 280px;
  max-width: 400px;
  border: 1px solid #e0e0e0;
  .search-bar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    border-bottom: 1px solid #e0e0e0;
    background-color: #fafafa;
    
    .search-icon {
      font-size: 14px;
      color: #999;
      flex-shrink: 0;
      line-height: 1;
    }
    
    .search-input-field {
      flex: 1;
      border: none;
      background: transparent;
      font-size: 13px;
      padding: 4px 0;
      color: #2c3e50;
      outline: none;
      
      &::placeholder {
        color: #999;
        font-size: 13px;
      }
      
      &:focus {
        color: #2c3e50;
      }
    }
    
    .close-btn {
      cursor: pointer;
      font-size: 18px;
      color: #999;
      padding: 2px;
      border-radius: 3px;
      transition: all 0.15s;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.06);
        color: #666;
      }
      
      &:active {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }
  ul {
    padding: 4px 0;
    margin: 0;
    list-style: none;
    max-height: 280px;
    overflow-y: auto;
    overflow-x: visible;
    @include scroll;
    .item {
      margin: 1px 0;
      padding: 8px 16px;
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      min-height: 36px;
      position: relative;
      transition: background-color 0.12s ease;
      &:hover {
        background-color: rgba(0, 130, 243, 0.06);
      }
      &:active {
        background-color: rgba(0, 130, 243, 0.1);
      }
      &.is-selected {
        background-color: rgba(0, 130, 243, 0.08);
        font-weight: 500;
      }
      span {
        margin: 0;
        line-height: 1.4;
        flex: 1;
        user-select: none;
        font-size: 13px;
        color: #2c3e50;
      }
      ::v-deep .b-contain {
        display: table !important;
        position: relative !important;
        flex-shrink: 0 !important;
        width: 20px !important;
        height: 20px !important;
        min-width: 20px !important;
        min-height: 20px !important;
        margin: 0 !important;
        padding: 0 !important;
        cursor: pointer !important;
        overflow: visible !important;
        z-index: 1;
        .b-input {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 20px !important;
          height: 20px !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
      }
    }
    // Adjust padding when no checkbox (single-select mode)
    &.single-select-mode .item {
      padding-left: 16px;
    }
  }
}
</style>